import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { AlertTriangle, Barcode, Copy, Plus, RefreshCw, Search, ShieldCheck, PackageCheck, PackageOpen, Truck } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { isDeliveredStatus, describeTrackingStatus, ship24MilestoneLabel } from "@/lib/shipping/bgpost-payload";
import { showBgPostError } from "@/components/admin/BgPostErrorDialog";
import { addBarcodes, listBarcodes, refreshBarcodeStatuses, getTraceApiUsage, verifyBarcodeParcel, type BarcodeRow, type VerifySummary } from "@/lib/admin/barcodes.functions";
import { registerOrderParcel, reimportOrderParcel } from "@/lib/admin/bgpost.functions";
import { TrackingStatusPill as StatusPill } from "@/components/admin/TrackingStatusPill";
import { A1PostPanel } from "@/components/admin/A1PostPanel";
import { refreshA1PostStatuses } from "@/lib/shipping/a1post/a1post.functions";
import { BgPostVerifyDialog, VerifyBadge } from "@/components/admin/BgPostVerifyDialog";

export const Route = createFileRoute("/_authenticated/admin/barcodes")({
  head: () => ({
    meta: [
      { title: "Админ — Баркодове | FIXHEN" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminLayout>
      <CarrierTabs />
    </AdminLayout>
  ),
});

function CarrierTabs() {
  const [tab, setTab] = useState<"bgpost" | "a1post">("bgpost");
  return (
    <div className="space-y-5 max-md:p-3">
      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
        {([["bgpost", "BG POST", "bg-[#2563EB]"], ["a1post", "A1POST", "bg-orange-600"]] as const).map(([k, l, c]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold transition ${tab === k ? `${c} text-white shadow` : "text-slate-600 hover:bg-slate-100"}`}>
            <Truck className="h-4 w-4" />{l}
          </button>
        ))}
      </div>
      <RefreshAllButton />
      {tab === "bgpost" ? <BarcodesPage /> : <A1PostPanel />}
    </div>
  );
}

/**
 * One button for every courier: BG POST (+ Ship24/TraceAPI abroad) and A1POST.
 * Delivered parcels and ones already checked today are skipped to save capacity;
 * the per-row „Провери" buttons always check.
 */
function RefreshAllButton() {
  const qc = useQueryClient();
  const fnBg = useServerFn(refreshBarcodeStatuses);
  const fnA1 = useServerFn(refreshA1PostStatuses);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<string | null>(null);
  async function run() {
    setBusy(true);
    const [bg, a1] = await Promise.allSettled([fnBg({ data: {} }), fnA1({ data: {} })]);
    const errs: string[] = [];
    const parts: string[] = [];
    let skipped = 0;
    if (bg.status === "fulfilled") {
      const r = bg.value;
      parts.push(`BG POST: ${r.updated} обновени`);
      skipped += (r.skippedDelivered ?? 0) + (r.skippedToday ?? 0);
      errs.push(...r.errors.map((e) => `BG POST ${e}`));
      if (r.ship24Notice) toast.warning(`Международното проследяване е пропуснато: ${r.ship24Notice}`, { duration: 10000 });
      if (r.intlSkipped) toast.info(`${r.intlSkipped} пратки в чужбина са проверени в последните 12 ч — пропуснати, за да пестим TraceAPI заявки.`, { duration: 9000 });
    } else errs.push(`BG POST: ${(bg.reason as any)?.message ?? "Грешка"}`);
    if (a1.status === "fulfilled") {
      const r = a1.value;
      parts.push(`A1POST: ${r.updated} обновени${r.delivered ? ` (${r.delivered} доставени)` : ""}`);
      skipped += r.skippedDelivered + r.skippedToday;
      errs.push(...r.errors.map((e) => `A1POST ${e}`));
    } else errs.push(`A1POST: ${(a1.reason as any)?.message ?? "Грешка"}`);
    const summary = parts.join(" · ") + (skipped ? ` · пропуснати ${skipped} (доставени или проверени днес)` : "");
    setLast(summary);
    if (errs.length) showBgPostError({ title: `Проверка на статусите: ${errs.length} неуспешни`, message: summary + "\n\n" + errs.slice(0, 10).join("\n") + (errs.length > 10 ? `\n… и още ${errs.length - 10}` : "") });
    else toast.success(summary, { duration: 8000 });
    qc.invalidateQueries({ queryKey: ["admin", "barcodes"] });
    qc.invalidateQueries({ queryKey: ["admin", "a1post"] });
    qc.invalidateQueries({ queryKey: ["admin", "traceapi-usage"] });
    setBusy(false);
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button variant="outline" disabled={busy} onClick={run} title="BG POST + A1POST. Пропуска доставените и проверените днес.">
        <RefreshCw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Провери статус всички поръчки
      </Button>
      <span className="text-[12px] text-slate-500">{busy ? "Проверявам BG POST и A1POST…" : last ?? "Пропуска доставените и проверените днес пратки."}</span>
    </div>
  );
}

type Filter = "all" | "free" | "used" | "delivered";

const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("bg-BG", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

// Same rule the server uses, so counters/filters match the status refresh.
const isDelivered = (s: string | null) => isDeliveredStatus(s);

function BarcodesPage() {
  const fn = useServerFn(listBarcodes);
  const q = useQuery({ queryKey: ["admin", "barcodes"], queryFn: () => fn() });
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const rows = q.data ?? [];
  const qc = useQueryClient();
  const fnAdd = useServerFn(addBarcodes);
  const fnRefresh = useServerFn(refreshBarcodeStatuses);
  const fnUsage = useServerFn(getTraceApiUsage);
  const usageQ = useQuery({ queryKey: ["admin", "traceapi-usage"], queryFn: () => fnUsage(), refetchInterval: 60000 });
  const [checking, setChecking] = useState<number | null>(null);
  async function checkOne(row: BarcodeRow) {
    setChecking(row.id);
    try {
      const r = await fnRefresh({ data: { ids: [row.id], force: true } });
      if (r.errors.length) showBgPostError({ title: `Проверката на ${row.tracking_number} не мина`, message: r.errors.join("\n"), orderNumber: row.order_number });
      else toast.success(`${row.tracking_number}: статусът е обновен` + (r.intlChecked ? " (вкл. в чужбина)" : ""));
      if (r.ship24Notice) toast.warning(r.ship24Notice, { duration: 8000 });
      qc.invalidateQueries({ queryKey: ["admin", "barcodes"] });
      qc.invalidateQueries({ queryKey: ["admin", "traceapi-usage"] });
    } catch (e: any) { showBgPostError({ title: "Проверката не мина", message: e?.message ?? "Грешка", orderNumber: row.order_number }); }
    finally { setChecking(null); }
  }
  const [addOpen, setAddOpen] = useState(false);
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState<"add" | "verifyAll" | null>(null);
  const fnVerify = useServerFn(verifyBarcodeParcel);
  const [verifying, setVerifying] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ row: BarcodeRow; summary: VerifySummary | null; at: string | null } | null>(null);

  async function verifyOne(row: BarcodeRow, openAfter = true) {
    setVerifying(row.id);
    try {
      const r = await fnVerify({ data: { id: row.id } });
      if (openAfter) setDetail({ row, summary: r, at: r.verifiedAt });
      qc.invalidateQueries({ queryKey: ["admin", "barcodes"] });
      return r;
    } catch (e: any) {
      showBgPostError({ title: "BG POST: проверката на данните не мина", message: e?.message ?? "Грешка", orderNumber: row.order_number });
      return null;
    } finally { setVerifying(null); }
  }
  const fnRegister = useServerFn(registerOrderParcel);
  const [retrying, setRetrying] = useState<number | null>(null);
  async function retryRegister(row: BarcodeRow) {
    if (!row.order_id) return;
    setRetrying(row.id);
    try {
      const r: any = await fnRegister({ data: { orderId: row.order_id } });
      if (!r.ok) {
        showBgPostError({ title: "BG POST: регистрацията не мина", message: r.error, ref: r.ref, orderNumber: r.orderNumber ?? row.order_number });
        return;
      }
      toast.success(`#${row.order_number}: пратката е регистрирана в BG POST`);
      qc.invalidateQueries({ queryKey: ["admin", "barcodes"] });
      qc.invalidateQueries({ queryKey: ["admin", "orders"] });
    } catch (e: any) {
      showBgPostError({ title: "BG POST: грешка", message: e?.message ?? "Грешка", orderNumber: row.order_number });
    } finally { setRetrying(null); }
  }
  const fnReimport = useServerFn(reimportOrderParcel);
  const [reimporting, setReimporting] = useState(false);
  async function reimport(row: BarcodeRow) {
    setReimporting(true);
    try {
      const r: any = await fnReimport({ data: { barcodeId: row.id } });
      if (!r.ok) {
        showBgPostError({ title: "Import Again не мина", message: r.error, orderNumber: r.orderNumber ?? row.order_number, ref: r.ref });
        return;
      }
      setDetail({ row, summary: r.summary, at: r.verifiedAt });
      const bad = r.summary.diff + r.summary.missing;
      if (bad) toast.warning(`#${row.order_number}: изпратено наново, но остават ${bad} разлики`);
      else toast.success(`#${row.order_number}: изпратено наново — всички данни съвпадат`);
      qc.invalidateQueries({ queryKey: ["admin", "barcodes"] });
    } catch (e: any) {
      showBgPostError({ title: "Import Again не мина", message: e?.message ?? "Грешка", orderNumber: row.order_number });
    } finally { setReimporting(false); }
  }

  function openDetail(row: BarcodeRow) {
    if (row.bgpost_verify) setDetail({ row, summary: row.bgpost_verify, at: row.bgpost_verified_at });
    else void verifyOne(row);
  }
  async function verifyAll() {
    const list = rows.filter((r) => r.bgpost_parcel_guid && !isDelivered(r.status));
    setBusy("verifyAll");
    let good = 0, bad = 0, fail = 0;
    for (const r of list) {
      const v = await verifyOne(r, false);
      if (!v) fail++; else if (v.found && v.diff + v.missing === 0) good++; else bad++;
    }
    setBusy(null);
    if (bad || fail) toast.warning(`Проверени ${list.length}: ${good} точни · ${bad} с разлики · ${fail} неуспешни`);
    else toast.success(`Всички ${good} пратки са приети точно от BG POST`);
  }

  async function doAdd() {
    setBusy("add");
    try {
      const r = await fnAdd({ data: { text: paste } });
      toast.success(`Добавени: ${r.added}` + (r.duplicates ? ` · вече съществуващи: ${r.duplicates}` : ""));
      if (r.invalid.length) toast.warning(`Невалидни (${r.invalid.length}): ${r.invalid.slice(0, 5).join(", ")}`);
      setPaste(""); setAddOpen(false);
      qc.invalidateQueries({ queryKey: ["admin", "barcodes"] });
    } catch (e: any) { toast.error(e?.message ?? "Грешка"); } finally { setBusy(null); }
  }
  const stats = useMemo(() => {
    const used = rows.filter((r) => r.order_number);
    return {
      total: rows.length,
      free: rows.length - used.length,
      used: used.length,
      delivered: used.filter((r) => isDelivered(r.status)).length,
    };
  }, [rows]);

  const shown = rows.filter((r) => {
    if (filter === "free" && r.order_number) return false;
    if (filter === "used" && !r.order_number) return false;
    if (filter === "delivered" && !isDelivered(r.status)) return false;
    const s = search.trim().toUpperCase();
    return !s || r.tracking_number.includes(s) || (r.order_number ?? "").includes(s);
  });

  const cards: Array<{ key: Filter; label: string; value: number; icon: any; tone: string }> = [
    { key: "all", label: "Общо", value: stats.total, icon: Barcode, tone: "text-slate-900" },
    { key: "free", label: "Свободни", value: stats.free, icon: PackageOpen, tone: "text-emerald-600" },
    { key: "used", label: "Използвани", value: stats.used, icon: Truck, tone: "text-sky-600" },
    { key: "delivered", label: "Доставени", value: stats.delivered, icon: PackageCheck, tone: "text-slate-600" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <Barcode className="h-6 w-6 text-[#2563EB]" /> Баркодове
        </h1>
        <p className="mt-1 text-sm text-slate-500">Товарителници на Български пощи за изпращане на поръчки.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={() => setAddOpen(true)} className="bg-[#2563EB] hover:bg-[#1d4ed8]"><Plus className="mr-1.5 h-4 w-4" /> Добави номера</Button>
          <Button variant="outline" disabled={busy === "verifyAll" || !rows.some((r) => r.bgpost_parcel_guid)} onClick={verifyAll}>
            <ShieldCheck className={`mr-1.5 h-4 w-4 ${busy === "verifyAll" ? "animate-pulse" : ""}`} /> Провери данните в BG POST
          </Button>
          {usageQ.data && (
            <span className={`inline-flex items-center rounded-lg border px-3 text-[12px] ${usageQ.data.month >= usageQ.data.perMonth ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-600"}`} title="TraceAPI — международно проследяване (резервен вариант на Ship24). Всяка заявка се брои.">
              TraceAPI: {usageQ.data.month}/{usageQ.data.perMonth} този месец · {usageQ.data.minute}/{usageQ.data.perMinute} за минута
            </span>
          )}
        </div>
      </div>

      {!q.isLoading && stats.free < 5 && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4" /> Остават само {stats.free} свободни номера — добави нови, за да не спре издаването на товарителници.
        </div>
      )}

      <BgPostVerifyDialog
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        row={detail?.row ?? null}
        summary={detail?.summary ?? null}
        verifiedAt={detail?.at ?? null}
        busy={!!detail && verifying === detail.row.id}
        onRecheck={() => detail && void verifyOne(detail.row)}
        onReimport={() => detail && void reimport(detail.row)}
        reimporting={reimporting}
      />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Добави tracking номера</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Постави по един номер на ред (формат LA002434340BG). Интервалите се махат автоматично, дубликатите се пропускат.</p>
          <Textarea rows={10} value={paste} onChange={(e) => setPaste(e.target.value)} className="font-mono" placeholder={"LA002434340BG\nLA002434336BG"} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Отказ</Button>
            <Button disabled={busy === "add" || !paste.trim()} onClick={doAdd} className="bg-[#2563EB] hover:bg-[#1d4ed8]">Добави</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            className={`rounded-xl border bg-white p-4 text-left transition hover:shadow-sm ${filter === c.key ? "border-[#2563EB] ring-2 ring-[#2563EB]/15" : "border-slate-200"}`}
          >
            <div className="flex items-center justify-between text-[13px] text-slate-500">
              {c.label} <c.icon className={`h-4 w-4 ${c.tone}`} />
            </div>
            <div className={`mt-1 text-3xl font-semibold tabular-nums ${c.tone}`}>{c.value}</div>
          </button>
        ))}
      </div>

      {stats.total > 0 && (
        <div>
          <div className="mb-1 flex justify-between text-[12px] text-slate-500">
            <span>Използвани {stats.used} от {stats.total}</span>
            <span>{stats.free} оставащи</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-[#2563EB] transition-all" style={{ width: `${(stats.used / stats.total) * 100}%` }} />
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-200 p-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Търси товарителница или поръчка…" className="pl-9" />
          </div>
          <span className="ml-auto text-[12px] text-slate-500">{shown.length} реда</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[12px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">#</th>
                <th className="px-4 py-2.5 text-left font-medium">Tracking номер</th>
                <th className="px-4 py-2.5 text-left font-medium">Въведен</th>
                <th className="px-4 py-2.5 text-left font-medium">Поръчка</th>
                <th className="px-4 py-2.5 text-left font-medium">Използван</th>
                <th className="px-4 py-2.5 text-left font-medium">Статус</th>
                <th className="px-4 py-2.5 text-left font-medium">Данни в BG POST</th>
                <th className="px-4 py-2.5 text-left font-medium">Проверка</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.isLoading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Зареждане…</td></tr>
              )}
              {!q.isLoading && shown.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Няма резултати.</td></tr>
              )}
              {shown.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50/70">
                  <td className="px-4 py-3 tabular-nums text-slate-400">{r.id}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { navigator.clipboard.writeText(r.tracking_number); toast.success(`Копирано: ${r.tracking_number}`); }}
                      className="group inline-flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1 font-mono text-[13px] font-semibold tracking-wider text-slate-900 ring-1 ring-slate-200 hover:ring-[#2563EB]"
                      title="Копирай"
                    >
                      {r.tracking_number}
                      <Copy className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#2563EB]" />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.added_at)}</td>
                  <td className="px-4 py-3">
                    {r.order_number ? <span className="font-semibold tabular-nums text-[#2563EB]">#{r.order_number}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.used_at)}</td>
                  <td className="px-4 py-3"><StatusPill row={r} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <VerifyBadge row={r} busy={verifying === r.id} onClick={() => openDetail(r)} />
                      {r.order_id && !r.bgpost_parcel_guid && (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[12px]" disabled={retrying === r.id} onClick={() => retryRegister(r)} title="Изпрати данните към BG POST отново">
                          <RefreshCw className={`h-3.5 w-3.5 ${retrying === r.id ? "animate-spin" : ""}`} /> Опитай пак
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.order_number && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[12px]" disabled={checking !== null} onClick={() => checkOne(r)} title="Провери статуса само на тази товарителница (BG POST + в чужбина)">
                        <RefreshCw className={`h-3.5 w-3.5 ${checking === r.id ? "animate-spin" : ""}`} /> Провери
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
